import React, { useEffect, useState } from 'react';
import { Checkbox } from 'Components/Form';
import Icon from 'Components/Icon';
import EnvironmentManager from 'Utils/environmentManager';
import isEqual from 'lodash/isEqual';
import { Button } from 'Components/Button';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateUserBetSlipSettings,
  openDrawer,
  closeDrawer,
  betSlipSettingsOpened,
  betSlipSettingsUpdated,
  setBetSlipMultiplier,
} from 'Store/actions';
import { BETSLIP_DEFAULT_STAKE_QUICK_AMOUNTS } from 'Store/constants/betSlip';
import getDefaultStakeAmount, {
  clampDefaultStakeAmount,
  getDefaultStakeLimits,
  saveDefaultStakeAmount,
} from 'Utils/getDefaultStakeAmount';
import mixpanel from 'Utils/mixpanel';

const DEFAULT_STAKE_INPUT_MAX_LENGTH = 5;
const BACKEND_SETTING_KEYS = ['acceptHigherOdds', 'acceptLowerOdds', 'acceptLiveEvents', 'acceptPrematchEvents', 'acceptPredictaProbability'];

export default function BetSlipSettingsContent () {
  const fastBetDescription = EnvironmentManager.getMessageText({ key: 'betslip_bottom_sheet_fastbet_description' });
  const predictaSwitchName = EnvironmentManager.getMessageText({ key: 'predicta_betslip_settings_switch_name' });
  const predictaSwitch = EnvironmentManager.getMessageText({ key: 'predicta_switch' }) == 'true';
  const predictaClosingActionSwitch = EnvironmentManager.getMessageText({ key: 'predicta_closing_action_switch' }) == 'true';
  const settingsTitle = EnvironmentManager.getMessageText({ key: 'betslip_settings_title' }) || 'Ayarlar';
  const defaultStakeTitle = EnvironmentManager.getMessageText({ key: 'betslip_settings_default_stake_title' }) || 'Varsayılan Kupon Tutarı';
  const defaultStakeDescription = EnvironmentManager.getMessageText({ key: 'betslip_settings_default_stake_description' }) || 'Belirlediğiniz tutar, bir sonraki kombine kuponunuzda otomatik olarak gelir.';
  const dispatch = useDispatch();
  const authData = useSelector(state => state.auth.data);
  const coupon = useSelector(state => state.betSlip.coupon);
  const { acceptHigherOdds, acceptLowerOdds, acceptLiveEvents, acceptPrematchEvents, acceptPredictaProbability } = authData;
  const [savedDefaultStakeAmount] = useState(() => getDefaultStakeAmount());

  const [settings, setSettings] = useState({
    acceptHigherOdds: acceptHigherOdds,
    acceptLowerOdds: acceptLowerOdds,
    acceptLiveEvents: acceptLiveEvents,
    acceptPrematchEvents: acceptPrematchEvents,
    acceptPredictaProbability: acceptPredictaProbability,
    defaultStakeAmount: savedDefaultStakeAmount,
  });
  const [view, setView] = useState('settings');
  const [stakeInputValue, setStakeInputValue] = useState('');

  useEffect(() => {
    dispatch(betSlipSettingsOpened());
  }, []);

  useEffect(() => {
    if (view !== 'amount' || !window.visualViewport) return;

    const viewport = window.visualViewport;

    const updateKeyboardHeight = () => {
      const keyboardHeight = window.innerHeight - viewport.height - viewport.offsetTop;

      document.documentElement.style.setProperty('--betslip-keyboard-height', `${ Math.max(0, keyboardHeight) }px`);
    };

    updateKeyboardHeight();
    viewport.addEventListener('resize', updateKeyboardHeight);
    viewport.addEventListener('scroll', updateKeyboardHeight);

    return () => {
      viewport.removeEventListener('resize', updateKeyboardHeight);
      viewport.removeEventListener('scroll', updateKeyboardHeight);
      document.documentElement.style.removeProperty('--betslip-keyboard-height');
    };
  }, [view]);

  const handleBetslipSettingsChange = settingType => {
    setSettings(prev => ({
      ...prev,
      [settingType]: !prev[settingType],
    }));
  };

  const setDrawerHeaderVisible = isVisible => dispatch(openDrawer({
    title: isVisible ? settingsTitle : '',
    showCloseOnBottom: isVisible,
  }));

  const openDefaultStakeView = () => {
    setStakeInputValue('');
    setDrawerHeaderVisible(false);
    setView('amount');
  };

  const handleStakeInputChange = e => {
    setStakeInputValue(e.target.value.replace(/\D/g, '').slice(0, DEFAULT_STAKE_INPUT_MAX_LENGTH));
  };

  const handleQuickAmountClick = amount => {
    const currentValue = stakeInputValue === '' ? 0 : +stakeInputValue;
    const newValue = Math.min(currentValue + amount, getDefaultStakeLimits().max);

    setStakeInputValue(String(newValue));
  };

  const handleStakeConfirm = () => {
    // Alan boş onaylanırsa placeholder'da görünen mevcut değer korunur.
    if (stakeInputValue !== '') {
      setSettings(prev => ({ ...prev, defaultStakeAmount: clampDefaultStakeAmount(stakeInputValue) }));
    }

    setDrawerHeaderVisible(true);
    setView('settings');
  };

  // Mobil klavyedeki "Tamam/Git" tuşu Enter olarak gelir.
  const handleStakeInputKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
      handleStakeConfirm();
    }
  };

  const onBetslipSettingsUpdate = async () => {
    if (checkUpdateButtonState()) return;

    const { defaultStakeAmount: newDefaultStakeAmount, ...backendSettings } = settings;
    const isBackendSettingsChanged = BACKEND_SETTING_KEYS.some(key => authData[key] !== backendSettings[key]);

    if (isBackendSettingsChanged) {
      await dispatch(updateUserBetSlipSettings(backendSettings));
    }

    const isPredictaProbabilityChanged
      = acceptPredictaProbability !== settings.acceptPredictaProbability;

    if (isPredictaProbabilityChanged) {
      mixpanel.track('Change Status Winning Probability on Betslip Settings', {
        'Winning Probability Status': settings.acceptPredictaProbability,
      });
    }

    if (savedDefaultStakeAmount !== newDefaultStakeAmount) {
      saveDefaultStakeAmount(newDefaultStakeAmount);

      const betCount = coupon?.bets?.length || 0;
      const isCombineCoupon = !betCount || (coupon?.system?.length === 1 && coupon.system.includes(betCount));

      if (isCombineCoupon) {
        dispatch(setBetSlipMultiplier(newDefaultStakeAmount));
      }
    }

    dispatch(betSlipSettingsUpdated(settings));
    onBetslipSettingsClose();
  };

  const checkUpdateButtonState = () => {
    const oldSettings = {
      acceptHigherOdds,
      acceptLowerOdds,
      acceptLiveEvents,
      acceptPrematchEvents,
      acceptPredictaProbability,
      defaultStakeAmount: savedDefaultStakeAmount,
    };

    return isEqual(oldSettings, settings);
  };

  const onBetslipSettingsClose = () => {
    dispatch(closeDrawer());

    setSettings({
      acceptHigherOdds: acceptHigherOdds,
      acceptLowerOdds: acceptLowerOdds,
      acceptLiveEvents: acceptLiveEvents,
      acceptPrematchEvents: acceptPrematchEvents,
      acceptPredictaProbability: acceptPredictaProbability,
      defaultStakeAmount: savedDefaultStakeAmount,
    });
    setView('settings');
  };

  if (view === 'amount') {
    return (
      <div className="betslip__settings-sheet__amount-entry">
        <div className="betslip__settings-sheet__amount-entry__field">
          <div className="betslip__settings-sheet__amount-entry__field__label">{ defaultStakeTitle }</div>
          <div className="betslip__settings-sheet__amount-entry__field__input-wrapper">
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={ DEFAULT_STAKE_INPUT_MAX_LENGTH }
              autoFocus
              className="betslip__settings-sheet__amount-entry__field__input"
              value={ stakeInputValue }
              placeholder={ String(settings.defaultStakeAmount) }
              onChange={ handleStakeInputChange }
              onKeyDown={ handleStakeInputKeyDown }
            />
            <span>TL</span>
          </div>
        </div>

        <div className="betslip__settings-sheet__amount-entry__quick-amounts">
          { BETSLIP_DEFAULT_STAKE_QUICK_AMOUNTS.map(amount => (
            <button
              type="button"
              key={ amount }
              className="betslip__settings-sheet__amount-entry__quick-amounts__item"
              onClick={ () => handleQuickAmountClick(amount) }
            >
              { `+${ amount }` }
            </button>
          )) }

          <button
            type="button"
            className="betslip__settings-sheet__amount-entry__quick-amounts__confirm"
            onClick={ handleStakeConfirm }
          >
            Tamam
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="betslip__settings-sheet__section">
        <div className="betslip__settings-sheet__section__title">Hızlı Bahis</div>
        <div className="betslip__settings-sheet__section__description">{ fastBetDescription }</div>
        <div className="betslip__settings-sheet__checkboxes">
          <div className="betslip__settings-sheet__checkboxes__rate-raise">
            <Checkbox checked={ settings.acceptLiveEvents } onChange={ () => handleBetslipSettingsChange('acceptLiveEvents') } label="Canlı Bahisler" disabled={ false } />
          </div>

          <div className="betslip__settings-sheet__checkboxes__rate-raise">
            <Checkbox checked={ settings.acceptPrematchEvents } onChange={ () => handleBetslipSettingsChange('acceptPrematchEvents') } label="Maç Öncesi Bahisler" />
          </div>
        </div>
      </div>
      <div className="betslip__settings-sheet__section">
        <div className="betslip__settings-sheet__section__title">Oran Değişim Tercihleri</div>
        <div className="betslip__settings-sheet__section__description">Oran değişimleri tercihinize göre tüm kuponlarınız için uygulanacaktır.</div>
        <div className="betslip__settings-sheet__checkboxes">
          <div className="betslip__settings-sheet__checkboxes__rate-raise">
            <Checkbox checked={ settings.acceptHigherOdds } onChange={ () => handleBetslipSettingsChange('acceptHigherOdds') } label="Oran Artışını Kabul Et" disabled={ false } />
          </div>

          <div className="betslip__settings-sheet__checkboxes__rate-raise">
            <Checkbox checked={ settings.acceptLowerOdds } onChange={ () => handleBetslipSettingsChange('acceptLowerOdds') } label="Oran Düşüşünü Kabul Et" />
          </div>
        </div>
      </div>

      <div className="betslip__settings-sheet__section betslip__settings-sheet__section--no-border">
        <div className="betslip__settings-sheet__row" onClick={ openDefaultStakeView }>
          <div className="betslip__settings-sheet__row__text">
            <div className="betslip__settings-sheet__section__title">{ defaultStakeTitle }</div>
            <div className="betslip__settings-sheet__section__description">{ defaultStakeDescription }</div>
          </div>
          <div className="betslip__settings-sheet__row__value">
            <span>{ `${ settings.defaultStakeAmount } TL` }</span>
            <Icon className="betslip__settings-sheet__row__value__icon" iconName="svgArrowRightGray" />
          </div>
        </div>
      </div>

      { predictaSwitch && predictaClosingActionSwitch && (
        <div className='form__row form__row--checkbox margin-bottom-10'>
          <div className='form__row--checkbox__label'>
            <label className='predicta-settings-switch-label'>
              { predictaSwitchName }
            </label>
          </div>
          <div className='form__row--checkbox__input'>
            <Checkbox
              className='switch'
              checked={ settings.acceptPredictaProbability }
              onChange={ () => handleBetslipSettingsChange('acceptPredictaProbability') }
            />
          </div>
        </div>
      ) }
      <Button className="btn--primary btn--block betslip__settings-sheet__update-button" onClick={ () => onBetslipSettingsUpdate() } disabled={ checkUpdateButtonState() }>
        Güncelle
      </Button>
    </>
  );
}
